import { partial } from 'lodash';
import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import { useHistory } from 'react-router';
import React, { useState, useContext } from 'react';

import EditorButtons from './Buttons';
import { TemplateState } from '../Editor';
import { Heading } from '~/App/Components/Text';
import EditNameModal from '../Editor/EditNameModal';
import ConfirmationModal from '../../Components/ConfirmationModal';
import EditorContext, { EditorContextProps } from '../Editor/Context';
import useSaveMessageTemplate from '~/App/Modules/Settings/MessageTemplates/Pages/Editor/hooks/useSaveMessageTemplate';

const Chevron = styled.i`
  margin-right: 15px;
  color: #323232;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 20px;
  height: 65px;
  min-height: 65px;
  box-shadow: 0 2px 4px 0 rgba(159, 159, 159, 0.5);

  @media (max-width: 640px) {
    display: block;
    height: auto;
  }
`;

const TemplateName = styled(Heading)`
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 0.24px;

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

const BackButton = styled.button`
  border: unset;
  background-color: unset;
  padding: unset;
  :focus {
    outline: 0;
  }
`;

const Header = React.forwardRef<HTMLDivElement>((_, ref) => {
  const context: EditorContextProps = useContext(EditorContext);
  const { hasChanges } = context;
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const history = useHistory();

  const [isSaving, saveMessageTemplate] = useSaveMessageTemplate(context);

  const handleSaveAndClose = async (shouldSave: boolean = true) => {
    if (hasChanges && shouldSave) {
      await saveMessageTemplate(undefined, {
        onSuccess: () => history.push('/message-templates'),
      });
    } else if (!shouldSave) {
      history.push('/message-templates');
    }
  };

  const name =
    context.get('state') === TemplateState.Demo
      ? 'Demo template'
      : context.get('name') || 'Name this template';

  const handleBackButton = () => {
    if (context.hasChanges) {
      setConfirmationModalOpen(true);
    } else {
      history.push('/message-templates');
    }
  };

  return (
    <HeaderContainer ref={ref}>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        onConfirm={handleSaveAndClose}
        isLoading={isSaving}
      />
      <div>
        <TemplateName>
          <BackButton onClick={handleBackButton}>
            <Chevron className='fas fa-chevron-left'></Chevron>
          </BackButton>
          {name}{' '}
          {context.get('state') !== TemplateState.Demo && (
            <React.Fragment>
              <Button
                bsStyle='link-icon'
                style={{ fontSize: 16, padding: 0, marginLeft: 10 }}
                onClick={partial(setNameModalOpen, true)}
              >
                <i className='far fa-edit'></i>
                <span className='sr-only'>Edit name</span>
              </Button>
              {nameModalOpen && (
                <EditNameModal
                  isOpen={nameModalOpen}
                  onRequestClose={partial(setNameModalOpen, false)}
                  onConfirm={partial(context.set, 'name')}
                  defaultValue={context.get('name')}
                />
              )}
            </React.Fragment>
          )}{' '}
        </TemplateName>
      </div>
      <EditorButtons
        isSaving={isSaving}
        onPrimaryButtonClick={handleSaveAndClose}
      />
    </HeaderContainer>
  );
});

Header.displayName = 'Header';

export default Header;
